import React, { useState, useEffect, useRef } from 'react';
import analyticsService from '../services/analyticsService';
import googleAnalytics from '../services/googleAnalytics';
import analyticsIcon from '../assets/images/glassimage.png';
import positionsIcon from '../assets/images/graphimage.png';
import overviewIcon from '../assets/images/dollarbil.png';
import './MobileFullFinancial.css';
import '../styles/mobile-financial-visibility-improvements.css';
import '../styles/mobile-warning-icon-killer.css';

const MobileFullFinancial = ({ 
  detailedPortfolio, 
  loading, 
  onClose 
}) => {
  const [activeTab, setActiveTab] = useState(() => {
    // Check if there's a stored tab preference
    const storedTab = localStorage.getItem('financialDashboardActiveTab');
    if (storedTab) {
      localStorage.removeItem('financialDashboardActiveTab'); // Clear after reading
      return parseInt(storedTab, 10);
    }
    return 0; // Default to overview tab
  });
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [followUpResponse, setFollowUpResponse] = useState(null);
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const containerRef = useRef(null);
  const tabsRef = useRef(null);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: overviewIcon, isImage: true },
    { id: 'positions', label: 'Positions', icon: positionsIcon, isImage: true },
    { id: 'analytics', label: 'Analytics', icon: analyticsIcon, isImage: true }
  ];

  // Fetch analysis when analytics tab is selected
  useEffect(() => {
    if (activeTab === 2 && detailedPortfolio && !analysis) {
      // Track analytics view
      googleAnalytics.trackGame.viewAnalytics();
      fetchAnalysis();
    }
  }, [activeTab, detailedPortfolio, analysis]);

  const fetchAnalysis = async () => {
    setAnalysisLoading(true);
    try {
      const result = await analyticsService.analyzePortfolio(detailedPortfolio);
      if (result.success) {
        setAnalysis(result.data);
      } else {
        const sampleAnalysis = analyticsService.generateSampleAnalysis(detailedPortfolio);
        setAnalysis(sampleAnalysis);
      }
    } catch (error) {
      console.error('Error fetching analysis:', error);
      const sampleAnalysis = analyticsService.generateSampleAnalysis(detailedPortfolio);
      setAnalysis(sampleAnalysis);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handleFollowUpQuestion = async () => {
    if (!followUpQuestion.trim()) return;
    
    setFollowUpLoading(true);
    try {
      // Track analytics question asked
      googleAnalytics.trackGame.askQuestion('portfolio_analytics');
      
      const result = await analyticsService.askFollowUpQuestion(detailedPortfolio, followUpQuestion, analysis);
      if (result.success) {
        setFollowUpResponse(result.data);
      } else {
        const sampleResponse = {
          answer: `Based on your portfolio data, regarding "${followUpQuestion}": This is a great question! ${followUpQuestion.toLowerCase().includes('risk') ? 'Your current risk level is low due to your balanced approach.' : followUpQuestion.toLowerCase().includes('invest') ? 'Consider diversifying your investments based on your risk tolerance.' : 'I recommend reviewing your portfolio regularly and staying informed about market trends.'}`
        };
        setFollowUpResponse(sampleResponse);
      }
    } catch (error) {
      console.error('Error with follow-up question:', error);
      const sampleResponse = {
        answer: `Based on your portfolio data, regarding "${followUpQuestion}": This is a great question! ${followUpQuestion.toLowerCase().includes('risk') ? 'Your current risk level is low due to your balanced approach.' : followUpQuestion.toLowerCase().includes('invest') ? 'Consider diversifying your investments based on your risk tolerance.' : 'I recommend reviewing your portfolio regularly and staying informed about market trends.'}`
      };
      setFollowUpResponse(sampleResponse);
    } finally {
      setFollowUpLoading(false);
    }
  };


  const formatCurrency = (value) => {
    if (!value && value !== 0) return '$0.00';
    return `$${value.toFixed(2)}`;
  };

  const formatCompactCurrency = (value) => {
    if (!value && value !== 0) return '$0';
    // Always show full dollar amounts (no K or M formatting)
    return `$${Math.round(value).toLocaleString()}`;
  };

  // Add better fallback handling for mobile devices
  if (!detailedPortfolio && !loading) {
    console.log('❌ No portfolio data and not loading - showing fallback');
    return (
      <div className="mobile-dashboard-overlay">
        <div className="mobile-dashboard-container">
          <div className="mobile-dashboard-header">
            <div className="header-content">
              <h1 className="dashboard-title" style={{ color: '#111827' }}>Financial Dashboard</h1>
              <button onClick={onClose} className="close-btn">✕</button>
            </div>
          </div>
          <div className="mobile-dashboard-content">
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 20px',
              textAlign: 'center',
              gap: '16px',
              minHeight: '300px'
            }}>
              <div style={{ fontSize: '48px', opacity: 0.6 }}>💼</div>
              <p style={{ color: '#6b7280', fontSize: '16px', fontWeight: '500', margin: 0 }}>
                No portfolio data available
              </p>
              <button 
                onClick={() => window.location.reload()}
                style={{
                  padding: '12px 24px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-dashboard-overlay">
      <div 
        ref={containerRef}
        className="mobile-dashboard-container"
      >
        {/* Header */}
        <div className="mobile-dashboard-header">
          <div className="header-content">
            <h1 className="dashboard-title" style={{ color: '#111827' }}>Financial Dashboard</h1>
            <button onClick={onClose} className="close-btn">✕</button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div ref={tabsRef} className="mobile-dashboard-tabs">
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(index)}
              className={`tab-btn ${activeTab === index ? 'active' : ''}`}
            >
              <span className="tab-icon">
                {tab.isImage ? (
                  <img 
                    src={tab.icon} 
                    alt={tab.label}
                    style={{ 
                      width: '40px', 
                      height: '40px',
                      filter: activeTab === index ? 'brightness(1.2)' : 'grayscale(1) opacity(0.7)',
                      display: 'block',
                      margin: '0 auto'
                    }}
                  />
                ) : (
                  tab.icon
                )}
              </span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div className="mobile-dashboard-content">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading Portfolio Analytics...</p>
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 0 && (
                <div className="tab-content overview-tab">
                  {/* Hero Metrics */}
                  <div className="hero-metrics">
                    <div className="hero-metric">
                      <div className="hero-value">
                        {formatCompactCurrency(detailedPortfolio?.netWorth || 0)}
                      </div>
                      <div className="hero-label">
                        Net Worth
                      </div>
                    </div>
                  </div>

                  {/* Key Metrics Grid */}
                  <div className="metrics-section">
                    <h3 className="section-title">Quick Stats</h3>
                    <div className="metrics-grid">
                      <div className="metric-card cash">
                        <div className="metric-info">
                          <div className="metric-label">Cash</div>
                          <div className="metric-value">
                            {formatCompactCurrency(detailedPortfolio?.cash || 0)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="metric-card portfolio">
                        <div className="metric-info">
                          <div className="metric-label">Portfolio</div>
                          <div className="metric-value">
                            {formatCompactCurrency(detailedPortfolio?.totalAssetsValue || 0)}
                          </div>
                          <div style={{ 
                            display: 'flex', 
                            gap: '8px', 
                            marginTop: '12px',
                            width: '100%'
                          }}>
                            <button 
                              onClick={() => setActiveTab(1)}
                              style={{
                              flex: 1,
                              padding: '8px 12px',
                              border: '2px solid #3b82f6',
                              borderRadius: '0',
                              background: '#ffffff',
                              color: '#111827',
                              fontSize: '12px',
                              fontWeight: '700',
                              textTransform: 'uppercase',
                              letterSpacing: '0.025em',
                              cursor: 'pointer',
                              boxShadow: '2px 2px 0px #333333'
                            }}>
                              Positions
                            </button>
                            <button 
                              onClick={() => window.location.href = '/mobile/trade'}
                              style={{
                              flex: 1,
                              padding: '8px 12px',
                              border: '2px solid #10b981',
                              borderRadius: '0',
                              background: '#ffffff',
                              color: '#111827',
                              fontSize: '12px',
                              fontWeight: '700',
                              textTransform: 'uppercase',
                              letterSpacing: '0.025em',
                              cursor: 'pointer',
                              boxShadow: '2px 2px 0px #333333'
                            }}>
                              Trade
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="metric-card savings">
                        <div className="metric-info">
                          <div className="metric-label">Savings</div>
                          <div className="metric-value">
                            {formatCompactCurrency(detailedPortfolio?.deposit || 0)}
                          </div>
                        </div>
                      </div>

                      <div className="metric-card loan">
                        <div className="metric-info">
                          <div className="metric-label">Loan</div>
                          <div className="metric-value">
                            {formatCompactCurrency(detailedPortfolio?.loan || 0)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* P&L Summary */}
                  <div className="pnl-section">
                    <h3 className="section-title">Performance</h3>
                    <div className={`pnl-card ${(detailedPortfolio?.totalRealizedPL || 0) >= 0 ? 'positive' : 'negative'}`}>
                      <div className="pnl-info">
                        <div className="pnl-label">Realized P&L</div>
                        <div className="pnl-value">
                          {formatCurrency(detailedPortfolio?.totalRealizedPL || 0)}
                        </div>
                      </div>
                    </div>
                    <div className={`pnl-card ${(detailedPortfolio?.totalUnrealizedPL || 0) >= 0 ? 'positive' : 'negative'}`} style={{ marginTop: '12px' }}>
                      <div className="pnl-info">
                        <div className="pnl-label">Unrealized P&L</div>
                        <div className="pnl-value">
                          {formatCurrency(detailedPortfolio?.totalUnrealizedPL || 0)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Positions Tab */}
              {activeTab === 1 && (
                <div className="tab-content positions-tab">
                  <h3 className="section-title">Investment Positions</h3>
                  
                  {detailedPortfolio?.positions && detailedPortfolio.positions.length > 0 ? (
                    <div className="positions-list">
                      {detailedPortfolio.positions.map((position, index) => (
                        <div key={index} className={`position-card ${(position.unrealizedPL || 0) >= 0 ? 'positive' : 'negative'}`}>
                          <div className="position-header">
                            <div className="position-symbol">{position.assetSymbol}</div>
                            <div className="position-quantity">{position.quantity} shares</div>
                          </div>
                          
                          <div className="position-details">
                            <div className="position-row">
                              <span className="detail-label">Avg Cost</span>
                              <span className="detail-value">{formatCurrency(position.averageCost || 0)}</span>
                            </div>
                            <div className="position-row">
                              <span className="detail-label">Current</span>
                              <span className="detail-value">{formatCurrency(position.currentPrice || 0)}</span>
                            </div>
                            <div className="position-row">
                              <span className="detail-label">Total Cost</span>
                              <span className="detail-value">{formatCurrency(position.totalCost || 0)}</span>
                            </div>
                            <div className="position-row">
                              <span className="detail-label">Market Value</span>
                              <span className="detail-value market-value">{formatCurrency(position.currentValue || 0)}</span>
                            </div>
                          </div>
                          
                          <div className={`position-pnl ${(position.unrealizedPL || 0) >= 0 ? 'positive' : 'negative'}`}>
                            <div className="pnl-label">Unrealized P&L</div>
                            <div className="pnl-amount">
                              {formatCurrency(position.unrealizedPL || 0)}
                              {position.unrealizedPLPercent ? 
                                ` (${position.unrealizedPLPercent.toFixed(1)}%)` : 
                                ' (0.0%)'
                              }
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <div className="empty-icon">📈</div>
                      <p>No investment positions found</p>
                    </div>
                  )}
                </div>
              )}

              {/* Analytics Tab */}
              {activeTab === 2 && (
                <div className="tab-content analytics-tab">
                  <h3 className="section-title">Portfolio Analytics</h3>
                  
                  {analysisLoading ? (
                    <div className="loading-state">
                      <div className="loading-spinner"></div>
                      <p>Analyzing your portfolio...</p>
                    </div>
                  ) : analysis ? (
                    <div className="analytics-content">
                      {/* Portfolio Summary */}
                      <div className="analytics-card">
                        <h4 className="analytics-subtitle">Summary</h4>
                        <p className="summary-text">{analysis.summary}</p>
                      </div>

                      {/* Strengths */}
                      <div className="analytics-card strengths">
                        <h4 className="analytics-subtitle positive">💪 Strengths</h4>
                        <ul className="insights-list">
                          {analysis.strengths.map((strength, index) => (
                            <li key={index} className="insight-item positive">
                              ✓ {strength}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Recommendations */}
                      <div className="analytics-card recommendations">
                        <h4 className="analytics-subtitle warning">Recommendations</h4>
                        <ul className="insights-list">
                          {analysis.recommendations.map((rec, index) => (
                            <li key={index} className="insight-item warning">
                              → {rec}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Risk Assessment */}
                      <div className="analytics-card risk-assessment">
                        <h4 className="analytics-subtitle">⚡ Risk Assessment</h4>
                        <div className="risk-level">
                          <span className="risk-label">Risk Level:</span>
                          <span className={`risk-badge ${analysis.riskAssessment.level.toLowerCase()}`}>
                            {analysis.riskAssessment.level}
                          </span>
                        </div>
                        <ul className="risk-factors">
                          {analysis.riskAssessment.factors.map((factor, index) => (
                            <li key={index} className="risk-factor">• {factor}</li>
                          ))}
                        </ul>
                      </div>

                      {/* Q&A Section */}
                      <div className="analytics-card qa-section">
                        <h4 className="analytics-subtitle">💬 Ask Questions</h4>
                        <div className="qa-input">
                          <input
                            type="text"
                            placeholder="Ask about your portfolio..."
                            value={followUpQuestion}
                            onChange={(e) => setFollowUpQuestion(e.target.value)}
                            className="question-input"
                            onKeyDown={(e) => e.key === 'Enter' && handleFollowUpQuestion()}
                          />
                          <button
                            onClick={handleFollowUpQuestion}
                            disabled={followUpLoading || !followUpQuestion.trim()}
                            className="ask-btn"
                          >
                            {followUpLoading ? '⏳' : '📤'}
                          </button>
                        </div>
                        
                        {followUpResponse && (
                          <div className="qa-response">
                            <div className="response-label">AI Response:</div>
                            <div className="response-text">{followUpResponse.answer}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <div className="empty-icon">🔍</div>
                      <p>Click to analyze your portfolio</p>
                      <button onClick={fetchAnalysis} className="analyze-btn">
                        Start Analysis
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileFullFinancial;